/**
 * User Settings V2 — "Notifications" section: default per-alarm
 * notification channels, system-email opt-ins, the daily alarm-report
 * digest, and the per-AlarmCategory subscription overrides.
 *
 * Contains:
 * - NotificationsSection — the rendered SettingsSection (default export)
 * - AlarmCategoriesSubsection — per-category channel overrides
 */

import NotificationsIcon from '@mui/icons-material/Notifications';
import { Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AlarmTree, groupAlarmsByParent } from '@sophocles/components/AlarmTree';
import { loadReference, selectAlarmCategories } from '@sophocles/features/reference';
import { useAlarmTranslation } from '@sophocles/hooks/useAlarmTranslation';
import {
  SettingsFieldRow,
  SettingsSection,
  SettingsSubsection,
  SettingsSwitchField,
  SettingsTimePickerField,
} from '@sophocles/views/settings/components';

// Resolve a row's display name via the category's locale_key so that
// name strings come from the i18n catalog rather than inline fields on
// the subscription row (backend no longer serialises `name` on rows).
function resolveRowName(tAlarm, row, category) {
  if (category?.locale_key) {
    return tAlarm(category.locale_key, { defaultValue: category.display_name });
  }
  return `#${row.alarm_category_id}`;
}

function resolveRowDescription(tAlarm, row, category) {
  if (!category?.locale_key) return undefined;
  return tAlarm(`${category.locale_key}_description`, { defaultValue: category.description });
}

// Build an id→category map from the reference slice so alarm-category
// name/description resolution uses the shared catalog (locale_key,
// display_name, description) rather than a dedicated /alarmcategories/
// fetch.  loadReference() is dispatched on mount in AlarmCategoriesSubsection;
// the thunk is session-cached so repeated calls are no-ops.
function useAlarmCategoryMap() {
  const categories = useSelector(selectAlarmCategories);
  return React.useMemo(() => {
    const map = {};
    for (const cat of categories) {
      map[cat.id] = cat;
    }
    return map;
  }, [categories]);
}

// Per-AlarmCategory subscription editor.  Mounted as a subsection
// inside the Notifications section — same place users manage
// where they get notified, just one level deeper for per-category
// overrides.
//
// Tree shape comes from `category.parent_id` on the AlarmCategory
// record (post pliny 0074/0075).  Pre-migration the field is
// missing and every row is treated as a flat orphan, which
// preserves the old rendering.  See `<AlarmTree>` for the
// rendering — same component the site-settings alarms section uses.
function AlarmCategoriesSubsection({ hasLegacySms }) {
  const { t } = useTranslation();
  const tAlarm = useAlarmTranslation();
  const dispatch = useDispatch();
  const { values } = useFormikContext();
  const alarms = values.alarms || [];
  const categoryMap = useAlarmCategoryMap();

  // Ensure reference catalog is loaded.  The thunk is session-cached
  // (condition guard in the slice) so this is a no-op when the catalog
  // is already present (e.g. user visited a dashboard first).  Settings
  // can be deep-linked directly, so we cannot rely on SiteDashboardView
  // having dispatched this already.
  React.useEffect(() => {
    dispatch(loadReference());
  }, [dispatch]);

  // Single source of truth for which channels render.  Add new
  // channels (e.g. App) by appending here — header + every row pick
  // it up automatically.
  const channels = [{ key: 'email', label: t('userSettings.email', 'Email') }];
  if (hasLegacySms) {
    channels.push({ key: 'sms', label: t('userSettings.sms', 'SMS') });
  }

  const { groups, orphans } = React.useMemo(() => {
    const getParent = row => {
      const cat = categoryMap[row.alarm_category_id];
      const parentId = cat?.parent_id;
      if (parentId == null) return null;
      const parent = categoryMap[parentId];
      return {
        id: parentId,
        name: parent?.locale_key
          ? tAlarm(parent.locale_key, { defaultValue: parent.display_name })
          : parent?.display_name,
        description: parent?.locale_key
          ? tAlarm(`${parent.locale_key}_description`, { defaultValue: parent.description })
          : parent?.description,
        sortKey: parent?.display_order,
      };
    };
    const getRowSortKey = row => categoryMap[row.alarm_category_id]?.display_order;
    return groupAlarmsByParent(alarms, getParent, { getRowSortKey });
  }, [alarms, categoryMap, tAlarm]);

  return (
    <SettingsSubsection
      title={t('userSettings.v2.alarmCategories', 'Alarm Categories')}
      description={t(
        hasLegacySms
          ? 'userSettings.v2.alarmCategoriesDescriptionWithSms'
          : 'userSettings.v2.alarmCategoriesDescription',
        hasLegacySms
          ? 'Choose whether each alarm category notifies you by email and / or SMS.'
          : 'Choose whether each alarm category notifies you by email.',
      )}
      bordered
      flush
    >
      <AlarmTree
        alarms={alarms}
        groups={groups}
        orphans={orphans}
        channels={channels}
        emptyText={t('userSettings.noSubscriptions', 'No alarm subscriptions yet.')}
        rowKey={row => row.alarm_category_id}
        rowTitle={row => resolveRowName(tAlarm, row, categoryMap[row.alarm_category_id])}
        rowSubtitle={row => resolveRowDescription(tAlarm, row, categoryMap[row.alarm_category_id])}
      />
    </SettingsSubsection>
  );
}

export default function NotificationsSection({ hasLegacySms }) {
  const { t } = useTranslation();
  return (
    <SettingsSection
      id="settings-notifications"
      icon={<NotificationsIcon />}
      title={t('userSettings.v2.notifications', 'Notifications')}
      description={t(
        'userSettings.v2.notificationsDescription',
        'Where the portal sends per-alarm notifications, plus system emails and the daily alarm digest.',
      )}
    >
      <Stack spacing={3}>
        <SettingsSubsection
          title={t('userSettings.v2.channels', 'Channels')}
          description={t(
            'userSettings.v2.channelsDescription',
            'Default channels for per-alarm notifications. Override per category in the Alarm Categories section.',
          )}
          bordered
        >
          <SettingsFieldRow
            label={t('userSettings.emailNotifications', 'Email Notifications')}
            description={t(
              'userSettings.v2.emailNotificationsDescription',
              'Send alarm notifications to your account email address.',
            )}
            controlWidth={120}
          >
            <SettingsSwitchField name="notifications.via_email" hideLabel />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={`${t('userSettings.appNotifications', 'App Notifications')} (${t('userSettings.comingSoon', 'Coming Soon')})`}
            description={t(
              'userSettings.v2.appNotificationsDescription',
              'Push notifications via the mobile app — coming soon.',
            )}
            controlWidth={120}
            divider={hasLegacySms}
          >
            <SettingsSwitchField name="notifications.via_app" hideLabel disabled />
          </SettingsFieldRow>
          {hasLegacySms ? (
            <SettingsFieldRow
              label={t('userSettings.smsNotifications', 'SMS Notifications')}
              description={t(
                'userSettings.smsDeprecationWarning',
                'SMS notifications are being retired and will be removed in a future update. Please switch to an alternative notification method.',
              )}
              controlWidth={120}
              divider={false}
            >
              <SettingsSwitchField name="notifications.via_sms" hideLabel />
            </SettingsFieldRow>
          ) : null}
        </SettingsSubsection>

        <SettingsSubsection
          title={t('userSettings.v2.systemEmails', 'System Emails')}
          description={t(
            'userSettings.v2.systemEmailsDescription',
            'Status of the platform itself. Independent of per-alarm notifications.',
          )}
          bordered
        >
          <SettingsFieldRow
            label={t('userSettings.criticalSystemEmails', 'Critical System Emails')}
            description={t(
              'userSettings.v2.criticalSystemEmailsDescription',
              'Outages, security events, and other urgent platform issues.',
            )}
            controlWidth={120}
          >
            <SettingsSwitchField name="notifications.critical_system_emails" hideLabel />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('userSettings.infoSystemEmails', 'Informational System Emails')}
            description={t(
              'userSettings.v2.infoSystemEmailsDescription',
              'Product updates, feature announcements, and tips.',
            )}
            controlWidth={120}
          >
            <SettingsSwitchField name="notifications.info_system_emails" hideLabel />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('userSettings.reportEmails', 'Receive Operational Report Emails')}
            description={t('userSettings.v2.reportEmailsDescription', 'Periodic platform-health and usage reports.')}
            controlWidth={120}
            divider={false}
          >
            <SettingsSwitchField name="notifications.receive_report_emails" hideLabel />
          </SettingsFieldRow>
        </SettingsSubsection>

        <SettingsSubsection
          title={t('userSettings.v2.dailyAlarmReport', 'Daily Alarm Report')}
          description={t(
            'userSettings.v2.dailyAlarmReportDescription',
            'A once-per-day digest of alarms across the sites you can see.',
          )}
          bordered
        >
          <SettingsFieldRow
            label={t('userSettings.alarmReportEmails', 'Receive Daily Alarm Report Emails')}
            description={t(
              'userSettings.v2.alarmReportEmailsDescription',
              'Toggle on to receive the daily digest at your chosen delivery time.',
            )}
            controlWidth={120}
          >
            <SettingsSwitchField name="notifications.receive_alarm_report_emails" hideLabel />
          </SettingsFieldRow>
          <SettingsFieldRow
            label={t('userSettings.deliveryTime', 'Delivery Time')}
            description={t(
              'userSettings.v2.deliveryTimeDescription',
              'When the digest is sent each day, in your local time zone.',
            )}
            controlWidth={200}
            divider={false}
          >
            <SettingsTimePickerField name="notifications.alarm_report_email_hour" hiddenLabel />
          </SettingsFieldRow>
        </SettingsSubsection>

        <AlarmCategoriesSubsection hasLegacySms={hasLegacySms} />
      </Stack>
    </SettingsSection>
  );
}
